import { ReactiveController, ReactiveControllerHost } from 'lit';
import type { WorkspaceLayout, TabState, SplitDirection } from '@arctek/sysml-types';
import {
  createDefaultLayout,
  validateLayout,
  splitPane,
  closePane,
  moveTab,
  openTab,
  closeTab,
  updateSplitSizes,
  setActivePane,
  setActiveTab,
} from './layout-utils';

const STORAGE_KEY = 'sysml:workspace-layout';
const STORAGE_META_KEY = 'sysml:workspace-layout:meta';
const RECOVERY_KEY = 'sysml:workspace-layout:recovery';

export interface LayoutMeta {
  timestamp: number;
  sessionId: string;
  version: number;
}

export class LayoutController implements ReactiveController {
  private host: ReactiveControllerHost;
  private _layout: WorkspaceLayout;
  private sessionId: string;
  generation = 0;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    this.sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this._layout = this.loadLayout();
    host.addController(this);
  }

  hostConnected(): void {
    window.addEventListener('storage', this.handleStorageChange);
  }

  hostDisconnected(): void {
    window.removeEventListener('storage', this.handleStorageChange);
  }

  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY && event.newValue) {
      try {
        const remoteLayout = JSON.parse(event.newValue) as WorkspaceLayout;
        const remoteMeta = this.loadMeta();
        const localMeta = {
          timestamp: Date.now(),
          sessionId: this.sessionId,
          version: this._layout.version,
        };

        if (remoteMeta && remoteMeta.timestamp > localMeta.timestamp) {
          const error = validateLayout(remoteLayout);
          if (!error) {
            this._layout = remoteLayout;
            this.generation++;
            this.host.requestUpdate();
          }
        }
      } catch (e) {
        console.warn('Failed to parse remote layout update', e);
      }
    }
  };

  private loadLayout(): WorkspaceLayout {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return createDefaultLayout();
      }

      const layout = JSON.parse(stored) as WorkspaceLayout;
      const error = validateLayout(layout);

      if (error) {
        console.error('Layout validation failed:', error);
        this.saveRecovery(stored);
        this.emitStatus('warn', `Layout corrupted: ${error}. Using default layout.`);
        
        // Emit recovery telemetry
        const event = new CustomEvent('sysml-layout-event', {
          detail: { type: 'layout_restore_fallback', timestamp: Date.now(), error },
          bubbles: true,
          composed: true,
        });
        (this.host as unknown as HTMLElement).dispatchEvent(event);
        
        return createDefaultLayout();
      }

      if (layout.version !== 1) {
        const migrated = this.migrateLayout(layout);
        if (!migrated) {
          this.saveRecovery(stored);
          this.emitStatus('warn', 'Layout migration failed. Using default layout.');
          
          // Emit migration failure telemetry
          const event = new CustomEvent('sysml-layout-event', {
            detail: { type: 'layout_restore_fallback', timestamp: Date.now(), reason: 'migration_failed' },
            bubbles: true,
            composed: true,
          });
          (this.host as unknown as HTMLElement).dispatchEvent(event);
          
          return createDefaultLayout();
        }
        return migrated;
      }

      // Emit successful restore telemetry
      const event = new CustomEvent('sysml-layout-event', {
        detail: { type: 'layout_restore_success', timestamp: Date.now() },
        bubbles: true,
        composed: true,
      });
      (this.host as unknown as HTMLElement).dispatchEvent(event);

      return layout;
    } catch (e) {
      console.error('Failed to load layout:', e);
      this.emitStatus('error', 'Failed to load workspace layout. Using default.');
      return createDefaultLayout();
    }
  }

  private migrateLayout(layout: WorkspaceLayout): WorkspaceLayout | null {
    return null;
  }

  private saveLayout(): void {
    try {
      const serialized = JSON.stringify(this._layout);
      localStorage.setItem(STORAGE_KEY, serialized);

      const meta: LayoutMeta = {
        timestamp: Date.now(),
        sessionId: this.sessionId,
        version: this._layout.version,
      };
      localStorage.setItem(STORAGE_META_KEY, JSON.stringify(meta));
    } catch (e) {
      console.error('Failed to save layout:', e);
      this.emitStatus('error', 'Failed to save workspace layout');
    }
  }

  private loadMeta(): LayoutMeta | null {
    try {
      const stored = localStorage.getItem(STORAGE_META_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private saveRecovery(corruptedData: string): void {
    try {
      localStorage.setItem(RECOVERY_KEY, corruptedData);
    } catch (e) {
      console.error('Failed to save recovery data:', e);
    }
  }

  private emitStatus(type: 'success' | 'error' | 'warn' | 'info', message: string): void {
    const event = new CustomEvent('sysml-status', {
      detail: { type, message },
      bubbles: true,
      composed: true,
    });
    (this.host as unknown as HTMLElement).dispatchEvent(event);
  }

  private applyMutation(mutation: () => { success: boolean; layout?: WorkspaceLayout; error?: string }, eventName: string): boolean {
    const result = mutation();
    if (result.success && result.layout) {
      this._layout = result.layout;
      this.generation++;
      this.saveLayout();
      this.host.requestUpdate();
      
      // Emit telemetry event
      const event = new CustomEvent('sysml-layout-event', {
        detail: { type: eventName, timestamp: Date.now(), layout: this._layout },
        bubbles: true,
        composed: true,
      });
      (this.host as unknown as HTMLElement).dispatchEvent(event);
      
      return true;
    } else {
      console.error('Layout mutation failed:', result.error);
      this.emitStatus('error', result.error || 'Layout mutation failed');
      return false;
    }
  }

  get layout(): WorkspaceLayout {
    return this._layout;
  }

  splitPane(paneId: string, direction: SplitDirection): boolean {
    return this.applyMutation(() => splitPane(this._layout, paneId, direction), 'split_pane');
  }

  closePane(paneId: string): boolean {
    return this.applyMutation(() => closePane(this._layout, paneId), 'close_pane');
  }

  moveTab(tabId: string, targetPaneId: string, index?: number): boolean {
    return this.applyMutation(() => moveTab(this._layout, tabId, targetPaneId, index), 'move_tab');
  }

  openTab(config: TabState, targetPaneId?: string, forceDuplicate: boolean = false): boolean {
    return this.applyMutation(() => openTab(this._layout, config, targetPaneId, forceDuplicate), 'open_tab');
  }

  closeTab(tabId: string): boolean {
    return this.applyMutation(() => closeTab(this._layout, tabId), 'close_tab');
  }

  updateSplitSizes(splitId: string, sizes: [number, number]): boolean {
    return this.applyMutation(() => updateSplitSizes(this._layout, splitId, sizes), 'resize_pane');
  }

  setActivePane(paneId: string): boolean {
    return this.applyMutation(() => setActivePane(this._layout, paneId), 'set_active_pane');
  }

  setActiveTab(paneId: string, tabId: string): boolean {
    return this.applyMutation(() => setActiveTab(this._layout, paneId, tabId), 'set_active_tab');
  }
}
